import { type RefObject, useEffect, useState } from "react";
import { Icon } from "@/components/ui/Icon";

/**
 * "Nach oben", bound to a SPECIFIC scroll container.
 *
 * 🔴 It takes the container as a prop because Flow has more than one. `AppShell` makes
 * `<main>` the scroll container — but the Protokolle presentation overlay is a fixed,
 * full-screen layer with its OWN, and it sits at z-index 100. So the shell's button was both
 * bound to the wrong element AND painted underneath the overlay: it never appeared in
 * presentation mode, which is exactly where a long Protokoll needs it, in front of a room.
 *
 * Listens on the container rather than taking an `onScroll` prop, so a caller cannot wire it
 * to one element and read scroll from another.
 */
export function BackToTop({
  containerRef,
  zIndex = 45,
  threshold = 600,
}: {
  containerRef: RefObject<HTMLElement | null>;
  /** Must clear the surface it sits on. The presentation overlay is 100; portals start at 200. */
  zIndex?: number;
  threshold?: number;
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setShow(el.scrollTop > threshold);
    onScroll(); // the container may already be scrolled when this mounts (entering presentation)
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [containerRef, threshold]);

  if (!show) return null;

  return (
    <button
      type="button"
      title="Nach oben"
      aria-label="Nach oben"
      onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex,
        width: 40,
        height: 40,
        borderRadius: 999,
        border: "1px solid var(--hairline-strong)",
        background: "var(--surface-elev)",
        color: "var(--ink-2)",
        boxShadow: "0 6px 20px rgba(13,62,99,0.18)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon name="chevUp" size={18} />
    </button>
  );
}
